import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Eye } from "lucide-react";
import { API } from "../../App";
import { useAdminAuth } from "./AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";

const METRICS = [
  ["visit", "Visits"],
  ["play", "Song plays"],
  ["complete", "Full listens"],
  ["sheet_view", "Sheet views"],
  ["sheet_zoom", "Sheet zooms"],
  ["downloads", "PDF downloads"],
  ["cta_click", "Platform clicks"],
];

const BREAKDOWNS = [
  ["sources", "Came from"],
  ["countries", "Countries"],
  ["cities", "Cities"],
  ["devices", "Devices"],
  ["browsers", "Browsers"],
];

export const PreviewStatsCard = () => {
  const { token } = useAdminAuth();
  const [s, setS] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/preview-page/stats`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => setS(r.data)).catch(() => {});
  }, [token]);

  if (!s) return null;
  return (
    <Card data-testid="preview-stats-card">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-2 text-lg"><Eye className="h-5 w-5 text-gold" /> Preview Page Stats</CardTitle>
        <div className="flex items-center gap-2">
          <Badge variant={s.published ? "default" : "secondary"} data-testid="preview-stats-status">{s.published ? "Live" : "Hidden"}</Badge>
          <Link to="/admin/preview-page" className="text-xs text-gold hover:underline" data-testid="preview-stats-manage-link">Manage</Link>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {METRICS.map(([k, label]) => (
            <div key={k} className="rounded-lg border p-3" data-testid={`preview-stat-${k}`}>
              <p className="text-2xl font-bold">{s[k] ?? 0}</p>
              <p className="text-xs text-muted-foreground">{label}</p>
              {s[`${k}_7d`] !== undefined && <p className="text-[11px] text-muted-foreground">{s[`${k}_7d`]} in last 7 days</p>}
            </div>
          ))}
          <div className="rounded-lg border p-3" data-testid="preview-stat-unique">
            <p className="text-2xl font-bold">{s.unique_visitors}</p>
            <p className="text-xs text-muted-foreground">Unique visitors</p>
            <p className="text-[11px] text-muted-foreground">{s.unique_ips ?? 0} unique networks</p>
          </div>
        </div>
        {s.breakdowns && (
          <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-5" data-testid="preview-breakdowns">
            {BREAKDOWNS.map(([k, label]) => (
              <div key={k} data-testid={`preview-breakdown-${k}`}>
                <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
                {s.breakdowns[k]?.length ? (
                  <ul className="space-y-1 text-sm">
                    {s.breakdowns[k].map((r) => (
                      <li key={r.label} className="flex justify-between gap-2"><span className="truncate">{r.label}</span><span className="tabular-nums text-muted-foreground">{r.count}</span></li>
                    ))}
                  </ul>
                ) : <p className="text-xs text-muted-foreground">No data yet</p>}
              </div>
            ))}
          </div>
        )}
        <p className="mt-4 text-[10px] text-muted-foreground">
          Locations are approximate. <a href="https://db-ip.com" target="_blank" rel="noreferrer" className="underline">IP Geolocation by DB-IP</a>
        </p>
      </CardContent>
    </Card>
  );
};
